/**
 * The one error every backend failure becomes.
 *
 * - `http`: the backend answered with a non-2xx status (other than 401).
 * - `auth`: a 401, or no session token to send.
 * - `network`: no response arrived (`status` 0).
 * - `timeout`: no response arrived before the timeout (`status` 0).
 * - `contract`: a 2xx response did not match its schema (only under the `throw` policy).
 */
export type ApiErrorKind = 'http' | 'network' | 'timeout' | 'contract' | 'auth'

export interface ApiErrorInit {
  kind?: ApiErrorKind
  /** The backend's `error.type`, such as `'FORBIDDEN'`. */
  type?: string
  details?: unknown
  /** `'GET /v1/...'`, for logs and debugging. */
  endpoint?: string
  cause?: unknown
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  /** The HTTP status; 0 when no response arrived. */
  readonly status: number
  readonly type?: string
  readonly details?: unknown
  readonly endpoint: string

  /**
   * `(message, status)` is the legacy `BackendRequestError` signature, kept so
   * that class can become an alias of this one. Without an explicit `kind`, a
   * 401 is `auth`, another status is `http`, and 0 is `network`.
   */
  constructor(message: string, status = 0, init: ApiErrorInit = {}) {
    super(message, init.cause === undefined ? undefined : { cause: init.cause })
    this.name = 'ApiError'
    this.status = status
    this.kind = init.kind ?? (status === 401 ? 'auth' : status > 0 ? 'http' : 'network')
    this.type = init.type
    this.details = init.details
    this.endpoint = init.endpoint ?? ''
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError

/** True for the statuses that mean "not visible to the active school" (403, 404). */
export const isNotAvailableForActiveSchool = (error: unknown): boolean =>
  isApiError(error) && (error.status === 403 || error.status === 404)

/**
 * Re-raises a backend refusal as the domain error `toDomain` builds from it
 * (KTD8: error subclasses stay at the domain edge, built from `ApiError`'s
 * fields). A failure that never reached the backend (network, timeout) maps
 * too, with status 0. A `contract` mismatch is not a refusal and passes
 * through untouched.
 */
export async function refusalsAs<T>(call: Promise<T>, toDomain: (error: ApiError) => Error): Promise<T> {
  try {
    return await call
  } catch (error) {
    if (isApiError(error) && error.kind !== 'contract') throw toDomain(error)
    throw error
  }
}
